import { z } from "zod";

/**
 * Shape of the deployment configuration file (`environment/app-configs.json` on web, the bundled
 * copy on mobile). Web fetches it at runtime from a file a deployment can hand-edit, so the types
 * below are derived from the schema rather than asserted over `res.json()`.
 *
 * Two variants from one definition:
 * - `appConfigSchema` — lenient, for runtime. Unknown keys are dropped, not rejected: a file written
 *   for a newer bundle must not take an older one down.
 * - `strictAppConfigSchema` — rejects unknown keys at every level, for the files versioned in the
 *   repo, so a typo such as `appNmae` fails a test instead of silently doing nothing.
 */
const buildAppConfigSchema = (strict: boolean) => {
  const object = <T extends z.ZodRawShape>(shape: T) => (strict ? z.strictObject(shape) : z.object(shape));

  const quickPrompt = object({
    short: z.string().min(1),
    description: z.string().min(1),
    content: z.string().min(1),
  });

  const brandingStrings = object({
    appName: z.string().optional(),
    subtitle: z.string().optional(),
    description: z.string().optional(),
  });

  return object({
    /** Product name, header tagline and About line per language. Omitted keys keep @noa/i18n's. */
    branding: object({ en: brandingStrings.optional(), fr: brandingStrings.optional() }).optional(),
    chatOptions: object({ showReferenceDocumentsSummary: z.boolean() }),
    quickPrompts: object({ en: z.array(quickPrompt), fr: z.array(quickPrompt) }),
  });
};

export const appConfigSchema = buildAppConfigSchema(false);
export const strictAppConfigSchema = buildAppConfigSchema(true);

export type AppConfig = z.infer<typeof appConfigSchema>;
export type ChatOptions = AppConfig["chatOptions"];
export type QuickPrompts = AppConfig["quickPrompts"];
export type QuickPrompt = QuickPrompts["fr"][number];
